import { Component } from '@theme/component';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { morphSection } from '@theme/section-renderer';

/**
 * Loads recently viewed products into the section via Section Rendering API
 * (same id: search pattern as predictive search).
 */
class ZohRecentlyViewed extends Component {
  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;

  /** @type {AbortController | null} */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();

    if (this.dataset.loaded === 'true') return;

    this.#intersectionObserver = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0]?.isIntersecting) return;
        observer.disconnect();
        this.#load();
      },
      { rootMargin: '0px 0px 400px 0px' }
    );

    this.#intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver?.disconnect();
    this.#activeFetch?.abort();
  }

  async #load() {
    const { sectionId, productId, maxProducts } = this.dataset;
    if (!sectionId) return;

    const limit = Number(maxProducts) || 8;
    const currentId = productId ? String(productId) : '';
    const viewedProducts = RecentlyViewed.getProducts()
      .map(String)
      .filter((id) => id && id !== currentId)
      .slice(0, limit);

    if (viewedProducts.length === 0) {
      if (!window.Shopify?.designMode) this.#hide();
      return;
    }

    const url = new URL(Theme.routes.search_url, window.location.origin);
    url.searchParams.set('q', viewedProducts.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('section_id', sectionId);

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url.toString(), { signal: this.#activeFetch.signal });
      if (!response.ok) {
        this.#hide();
        return;
      }

      const html = await response.text();
      if (!html.trim()) {
        this.#hide();
        return;
      }

      this.dataset.loaded = 'true';
      await morphSection(sectionId, html, { mode: 'full', injectStylesheet: true });

      const section = document.getElementById(`shopify-section-${sectionId}`);
      const root = section?.querySelector('zoh-recently-viewed');
      const hasItems = Boolean(root?.querySelector('[data-zoh-rv-has-products="true"]'));
      if (!hasItems && root instanceof HTMLElement) {
        root.classList.add('hidden');
        const wrapper = root.closest('.shopify-section');
        if (wrapper instanceof HTMLElement) wrapper.hidden = true;
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      console.error('Recently viewed error:', error);
      this.#hide();
    } finally {
      this.#activeFetch = null;
    }
  }

  #hide() {
    this.classList.add('hidden');
    const wrapper = this.closest('.shopify-section');
    if (wrapper instanceof HTMLElement) wrapper.hidden = true;
  }
}

if (!customElements.get('zoh-recently-viewed')) {
  customElements.define('zoh-recently-viewed', ZohRecentlyViewed);
}
